/**
 * Persists which review run is "active" in the taskpane, in localStorage, so switching to
 * another Office pane (e.g. the native Comments pane) and back doesn't lose the current run —
 * the Office taskpane iframe remounts from scratch when it regains focus, wiping React state.
 *
 * localStorage is shared by origin, not per open document — with two files open at once, both
 * taskpanes read/write the exact same keys. Every key below is therefore namespaced by the
 * document's identity scope (documentIdentity.ts: an id stored inside the file, falling back to its
 * url for files that predate ids), so each open document only ever sees its own saved state.
 * A document that cannot be identified (unsaved, no id yet) stores and loads nothing: sharing a
 * bucket between unidentifiable documents is what leaked one deck's results into another.
 */

import { getDocumentScope } from './documentIdentity';

const STORAGE_KEY = 'qcbot.activeRun';
const SELECTION_STORAGE_KEY = 'qcbot.selection';

export type PersistedStage = 'running' | 'results';

export interface PersistedRunState {
  runId: string;
  stage: PersistedStage;
  /** The document scope the run was saved for; absent on records written before scoping by id. */
  scope?: string;
}

export function saveRunState(runId: string, stage: PersistedStage): void {
  const scope = getDocumentScope();
  if (!scope) return;
  localStorage.setItem(`${STORAGE_KEY}:${scope}`, JSON.stringify({ runId, stage, scope } satisfies PersistedRunState));
}

export function loadRunState(): PersistedRunState | null {
  const scope = getDocumentScope();
  if (!scope) return null;
  const raw = localStorage.getItem(`${STORAGE_KEY}:${scope}`);
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as PersistedRunState;
    // Ownership check: never render a run that was saved for a different document.
    if (state.scope !== undefined && state.scope !== scope) return null;
    return state;
  } catch {
    return null;
  }
}

export function clearRunState(): void {
  const scope = getDocumentScope();
  if (scope) localStorage.removeItem(`${STORAGE_KEY}:${scope}`);
}

/** Remembers the last project/template picked, so reopening the taskpane (which remounts React
 * state from scratch) doesn't force re-picking them on every single review. Only a convenience
 * default, so unidentifiable documents share one bucket. */
export interface PersistedSelection {
  projectId?: string;
  qcTemplateId?: string;
}

export function saveSelection(selection: PersistedSelection): void {
  localStorage.setItem(`${SELECTION_STORAGE_KEY}:${getDocumentScope() ?? 'untitled'}`, JSON.stringify(selection));
}

export function loadSelection(): PersistedSelection | null {
  const raw = localStorage.getItem(`${SELECTION_STORAGE_KEY}:${getDocumentScope() ?? 'untitled'}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedSelection;
  } catch {
    return null;
  }
}
